/**
 * FloatingPreviewWindow — picture-in-picture preview that survives any scroll.
 *
 * Portal-mounted at <body>, position: fixed. Three modes the user can toggle
 * from the header:
 *   • normal   — full PIP, draggable + resizable
 *   • minimised — collapsed to a compact chip in the corner ("Preview · Login")
 *   • closed   — unmounted; rail is the only preview again
 *
 * State is persisted per-user in localStorage (`wl-floating-preview`) so the
 * user's choice (size, position, mode, surface) sticks across sessions.
 *
 * Why floating instead of just a sticky rail: designers asked for a tiny
 * always-visible mirror that follows them as they scroll across long sections
 * of the editor, and that they can move out of the way. Sticky rails work for
 * that 80% of the time, but the long form (with anatomy diagram + N color
 * fields + N typography fields + N login features) routinely runs past the
 * rail's max-height. This solves that.
 *
 * RTL: when the document direction is rtl, the default corner mirrors to
 * bottom-left. Persisted positions are stored absolute (right/bottom) and
 * re-applied as-is on next mount.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { createPortal } from 'react-dom';
import { Tooltip } from 'antd';
import type { BrandConfig } from '@/brand/types';
import {
  PreviewScope,
  PreviewTabs,
  PREVIEW_SURFACE_LABEL_KEY,
  type PreviewSurface,
} from './PreviewSurfaces';
import './floating-preview.css';

/* ─── Persistence ──────────────────────────────────────────────────────── */

interface PersistedState {
  mode: 'normal' | 'minimised' | 'closed';
  surface: PreviewSurface;
  // Distances from the right and bottom edges of the viewport (px). We store
  // distances so the window stays in the same visual corner across viewport
  // resizes, instead of drifting off-screen on smaller laptops.
  right: number;
  bottom: number;
  width: number;
  height: number;
}

const STORAGE_KEY = 'wl-floating-preview';
const DEFAULT_STATE: PersistedState = {
  mode: 'closed',
  surface: 'login',
  right: 24,
  bottom: 24,
  width: 380,
  height: 320,
};
const MIN_W = 280;
const MIN_H = 220;
const MAX_W = 640;
const MAX_H = 520;

function loadState(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return { ...DEFAULT_STATE };
  }
}
function saveState(s: PersistedState) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

/* ─── Public API ───────────────────────────────────────────────────────── */

export interface FloatingPreviewWindowHandle {
  /** Pop out the rail's current preview into the floating window. */
  open: (surface?: PreviewSurface) => void;
  /** Force-set the active surface (e.g. when a field is focused). */
  setSurface: (surface: PreviewSurface) => void;
  /** Whether the window is currently rendered. */
  isOpen: () => boolean;
}

interface Props {
  merged: BrandConfig;
  /** Whether the parent wants to show the floating window. The header's "Pop out" / "Dock" toggles this. */
  open: boolean;
  onClose: () => void;
  /** Active surface in the parent's docked rail; the floating window mirrors it. */
  surface: PreviewSurface;
  onSurfaceChange: (s: PreviewSurface) => void;
  /** Pulse the window briefly when the merged brand changes (matches rail). */
  pulse?: boolean;
}

export default function FloatingPreviewWindow({
  merged,
  open,
  onClose,
  surface,
  onSurfaceChange,
  pulse = false,
}: Props) {
  const { t } = useLanguage();
  const [persisted, setPersisted] = useState<PersistedState>(() => loadState());
  // mode is local but synced to persisted on change
  const mode = open ? persisted.mode === 'closed' ? 'normal' : persisted.mode : 'closed';

  // Sync persisted surface when parent changes the surface.
  useEffect(() => {
    if (persisted.surface !== surface) {
      const next = { ...persisted, surface };
      setPersisted(next);
      saveState(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface]);

  /* ─── Drag ─────────────────────────────────────────────────────────── */

  const dragRef = useRef<{ startX: number; startY: number; startRight: number; startBottom: number } | null>(null);
  const onDragStart = useCallback((e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest('button, .ant-tabs-tab, input, .wl-fpw-resize')) return;
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startRight: persisted.right,
      startBottom: persisted.bottom,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [persisted.right, persisted.bottom]);
  const onDragMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const isRTL = document.documentElement.dir === 'rtl';
    const right = Math.max(8, dragRef.current.startRight - (isRTL ? -dx : dx));
    const bottom = Math.max(8, dragRef.current.startBottom - dy);
    setPersisted(p => ({ ...p, right, bottom }));
  }, []);
  const onDragEnd = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    setPersisted(p => { saveState(p); return p; });
  }, []);

  /* ─── Resize (bottom-end corner) ──────────────────────────────────── */

  const resizeRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);
  const onResizeStart = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: persisted.width,
      startH: persisted.height,
    };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, [persisted.width, persisted.height]);
  const onResizeMove = useCallback((e: React.PointerEvent) => {
    if (!resizeRef.current) return;
    const dx = e.clientX - resizeRef.current.startX;
    const dy = e.clientY - resizeRef.current.startY;
    const isRTL = document.documentElement.dir === 'rtl';
    // When docked to the right edge in LTR, dragging the SE corner right
    // should grow the window; in RTL the corner is on the left so flip.
    const width = Math.min(MAX_W, Math.max(MIN_W, resizeRef.current.startW + (isRTL ? -dx : dx)));
    const height = Math.min(MAX_H, Math.max(MIN_H, resizeRef.current.startH + dy));
    setPersisted(p => ({ ...p, width, height }));
  }, []);
  const onResizeEnd = useCallback((e: React.PointerEvent) => {
    if (!resizeRef.current) return;
    resizeRef.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    setPersisted(p => { saveState(p); return p; });
  }, []);

  /* ─── Mode helpers ─────────────────────────────────────────────────── */

  function setMode(next: PersistedState['mode']) {
    const updated = { ...persisted, mode: next };
    setPersisted(updated);
    saveState(updated);
    if (next === 'closed') onClose();
  }

  // When the parent's `open` flips off, persist that as `closed` so reopening
  // later starts in `normal` mode.
  useEffect(() => {
    if (!open && persisted.mode !== 'closed') {
      const updated = { ...persisted, mode: 'closed' as const };
      setPersisted(updated);
      saveState(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open || mode === 'closed') return null;

  const isRTL = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';

  if (mode === 'minimised') {
    const chipStyle: React.CSSProperties = {
      position: 'fixed',
      bottom: persisted.bottom,
      [isRTL ? 'left' : 'right']: persisted.right,
      zIndex: 'var(--z-chrome-float)',
    };
    return createPortal(
      <button
        type="button"
        className="wl-fpw-chip"
        style={chipStyle}
        onClick={() => setMode('normal')}
        aria-label={t('designSystem.restorePreview')}
      >
        <span className="wl-fpw-chip-dot" />
        {t('designSystem.previewSurface', { surface: t(PREVIEW_SURFACE_LABEL_KEY[surface]) })}
      </button>,
      document.body,
    );
  }

  const windowStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: persisted.bottom,
    [isRTL ? 'left' : 'right']: persisted.right,
    width: persisted.width,
    height: persisted.height,
    zIndex: 'var(--z-chrome-float)',
  };

  return createPortal(
    <div
      className={`wl-fpw ${pulse ? 'wl-fpw--pulse' : ''}`}
      style={windowStyle}
      role="dialog"
      aria-label={t('designSystem.floatingPreview')}
    >
      <header
        className="wl-fpw-header"
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
      >
        <span className="wl-fpw-header-title">
          <span className="wl-fpw-header-grip" aria-hidden>⋮⋮</span>
          {t('whiteLabel.livePreview')}
        </span>
        <div className="wl-fpw-header-actions">
          <Tooltip title={t('designSystem.minimiseToChip')}>
            <button type="button" className="wl-fpw-btn" onClick={() => setMode('minimised')} aria-label={t('designSystem.minimise')}>_</button>
          </Tooltip>
          <Tooltip title={t('designSystem.dockBack')}>
            <button type="button" className="wl-fpw-btn" onClick={() => setMode('closed')} aria-label={t('designSystem.dockAndClose')}>×</button>
          </Tooltip>
        </div>
      </header>
      <div className="wl-fpw-body">
        <PreviewScope merged={merged}>
          <PreviewTabs merged={merged} active={surface} onChange={onSurfaceChange} compact />
        </PreviewScope>
      </div>
      <span
        className="wl-fpw-resize"
        aria-label={t('designSystem.resizePreview')}
        onPointerDown={onResizeStart}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
        onPointerCancel={onResizeEnd}
      />
    </div>,
    document.body,
  );
}
